/**
 * Method Execution Service for dev-tools.
 *
 * Dispatches a method call against a registered service client, returning
 * a normalized success/failure result.
 */
import { supportServiceConnectClient } from "api";

import { AVAILABLE_SERVICES } from "../constants";
import { MethodInvocationResult } from "../types";
import { callOptionsHeaders } from "api/helpers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Service clients have different types from different protobuf packages
const SERVICE_CLIENTS: Record<string, any> = {
  support: supportServiceConnectClient,
};

export interface ExecuteMethodOptions {
  /** Drives the `x-region` header on the gRPC call. */
  region: string;
  /** Optional reference ID (typically a role/user id) for audit logging. */
  referenceId?: string;
}

export class MethodExecutionService {
  static async executeMethodForService(
    serviceId: string,
    methodName: string,
    formData: Record<string, unknown>,
    options: ExecuteMethodOptions
  ): Promise<MethodInvocationResult> {
    const startTime = Date.now();

    try {
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) {
        throw new Error(`Service ${serviceId} not found`);
      }

      const serviceClient = SERVICE_CLIENTS[serviceId];
      if (!serviceClient) {
        throw new Error(`Service client for ${serviceId} not available`);
      }

      const method = serviceDefinition.service.methods[methodName];
      if (!method?.I) {
        throw new Error(
          `Method ${methodName} not found in ${serviceId} service or has no input type`
        );
      }

      const request = new method.I(formData);
      const headers = callOptionsHeaders(options.region, options.referenceId);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic method invocation requires any type for service client methods
      const response = await (serviceClient as any)[methodName](request, {
        headers,
      });

      return {
        success: true,
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date(),
        methodName,
        requestData: formData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
        duration: Date.now() - startTime,
        timestamp: new Date(),
        methodName,
        requestData: formData,
      };
    }
  }
}
